// ─── Un'ora scritta in italiano, e l'orologio che resta ──────────────────────
//
// 🗣️ 17/09/2026, sua: *«Sistema anche le ore in italiano»*.
//
// 🚨 È la stessa malattia delle date, e lo stesso rimedio: un
// `<input type="time">` lo disegna il **browser** con la sua lingua, e dove
// quella lingua è l'inglese d'America esce `07:30 PM`. ⇒ Il campo diventa
// nostro — testo, `hh:mm`, ventiquattr'ore — e l'orologio del browser resta
// **sotto**, invisibile, al servizio del bottoncino qui accanto.
//
// ⚖️ E l'ora è **peggio** della data, se sbagliata: una data all'americana si
// legge male (`04/22` non esiste come giorno 4 del mese 22, quindi salta
// all'occhio), un'ora all'americana si legge **al contrario**. `07:30` senza
// il `PM` accanto è la mattina — e una fascia di listino che comincia la
// mattina invece che di sera sposta un prezzo su mezza giornata sbagliata,
// senza che niente si lamenti.
//
// 📌 *Le due cose che questo componente non fa sono scritte apposta: non
//    accetta le 24:00 (quella è la CHIUSURA, e ha il suo campo) e non prende
//    l'ora nuda mentre si scrive (⇒ `daOraItaliana`).*

import { useRef, useState } from 'react';
import { daOraItaliana, oraScritta } from '../lib/time.js';
import { SA_APRIRE_IL_CALENDARIETTO } from './CampoData.js';

export function CampoOra({
  etichetta,
  value,
  onChange,
  orologio = true,
  ...resto
}: {
  /** Con l'etichetta il campo si veste da `Campo`; senza, sta dove lo si mette. */
  etichetta?: string;
  /** Sempre `HH:MM` a ventiquattr'ore — `19:30` — o vuoto. */
  value: string;
  onChange(ora: string): void;
  /**
   * ⚠️ Il bottoncino si può togliere, e in un posto si toglie: nella frase
   * «il circolo apre alle … e chiude alle …» la seconda casella **non può**
   * averlo (accetta le 24:00, che un orologio di sistema non sa dire), e un
   * bottone su una sola delle due direbbe che le due caselle sono di natura
   * diversa. Lì sono due caselle scritte, uguali.
   */
  orologio?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>): React.JSX.Element {
  const sotto = useRef<HTMLInputElement>(null);
  const [testo, setTesto] = useState(() => oraScritta(value));
  /*
   * ⭐ Come in `CampoData`: si tiene da parte l'ultimo valore visto da fuori e
   * ci si risincronizza durante il disegno, invece che con un effetto. Un'ora
   * cambiata dal di fuori (l'orologio, un ripristino) arriva nel campo senza un
   * giro in cui il campo dice ancora quella vecchia.
   */
  const [visto, setVisto] = useState(value);
  if (value !== visto) {
    setVisto(value);
    setTesto(oraScritta(value));
  }

  /** Mentre si scrive servono i minuti: l'ora nuda vale solo uscendo. */
  function scrivi(grezzo: string): void {
    setTesto(grezzo);
    const ora = daOraItaliana(grezzo);
    if (ora !== null) {
      setVisto(ora);
      onChange(ora);
    }
  }

  function esci(): void {
    // ⭐ Il campo svuotato resta svuotato: in «Dalle»/«Alle» del listino vuoto
    //   vuol dire «ogni ora», che è una risposta buona.
    if (testo.trim() === '') {
      if (visto !== '') {
        setVisto('');
        onChange('');
      }
      return;
    }
    const ora = daOraItaliana(testo, true);
    // ⛔ Ciò che non è un'ora torna com'era: lasciare a schermo una scritta che
    //    nessuno ha preso farebbe credere che sia stata presa.
    if (ora === null) {
      setTesto(oraScritta(visto));
      return;
    }
    setTesto(ora);
    if (ora !== visto) {
      setVisto(ora);
      onChange(ora);
    }
  }

  // 🚨 `div` e non `span`: `.campo > span` veste le ETICHETTE, e vincerebbe per
  //    specificità mandando il bottoncino sotto al campo (già pagato una volta).
  const campo = (
    <div className="campo-ora">
      <input
        {...resto}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="hh:mm"
        value={testo}
        onChange={(e) => scrivi(e.target.value)}
        onBlur={esci}
      />
      {orologio && SA_APRIRE_IL_CALENDARIETTO && (
        <>
          <button
            type="button"
            className="apri-calendario"
            title="Scegli l’ora"
            aria-label="Scegli l’ora"
            disabled={resto.disabled}
            onClick={() => {
              try {
                sotto.current?.showPicker();
              } catch {
                sotto.current?.focus();
              }
            }}
          >
            {/* ⚠️ Disegnato e non scritto: un'emoji dipende dai caratteri
                installati, e un bottone con dentro un quadratino vuoto non dice
                cosa fa. */}
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <circle cx="8" cy="8" r="6.75" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="M8 4.2V8l2.6 1.9" fill="none" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
          {/* L'orologio vero resta nel documento — è lui che il browser apre — ma
              fuori dal giro del Tab: il comando è il campo scritto qui sopra. */}
          <input
            ref={sotto}
            type="time"
            className="data-sotto"
            value={oraScritta(value)}
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => e.target.value && onChange(e.target.value.slice(0, 5))}
          />
        </>
      )}
    </div>
  );

  if (etichetta === undefined) return campo;
  return (
    <label className="campo">
      <span>{etichetta}</span>
      {campo}
    </label>
  );
}
